import { Button, Drawer, Flex, message } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { isEligibleForOnboarding } from "../../../domain/user.domain";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux-hooks";
import { createCustomNote, getCustomNoteList, updateCustomNote } from "../../../service/notes.service";
import {
  setAITemplateModalState,
  setCustomTemplateSelectedTab,
  setStoredTemplateData,
  setTemplateOpenedFromNoteModal,
} from "../../../slices/appStateSlice";
import { setRecordingDetail } from "../../../slices/recordingsSlice";
import Create from "./Create";
import ListContainer from "./ListContainer";
import { type CustomNote, TabType } from "./types";

export const AITemplateModal = () => {
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const dispatch = useAppDispatch();
  const { isAITemplateModalVisible, storedTemplateData, customTemplateSelectedTab } = useAppSelector(
    (state) => state.appState,
  );
  const [isPrimaryButtonDisabled, setIsPrimaryButtonDisabled] = React.useState(true);
  const [currentTemplate, setCurrentTemplate] = React.useState<CustomNote | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const templateOpenedFromNoteModal = useAppSelector((state) => state.appState.templateOpenedFromNoteModal);
  const { recordingDetail } = useAppSelector((state) => state.recordings);
  const [isLoading, setIsLoading] = useState(false);
  const [customNoteList, setCustomNoteList] = useState<CustomNote[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const needsOnboarding = isEligibleForOnboarding(userInfo);

  const fetchCustomNoteList = useCallback(async () => {
    try {
      const response = await getCustomNoteList();
      setCustomNoteList(response.data);
      setInitialLoading(false);
    } catch (error) {
      console.error("Error fetching custom note list:", error);
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomNoteList();
  }, [isAITemplateModalVisible]);

  React.useEffect(() => {
    if ((needsOnboarding && userInfo.onboarding_data) || !needsOnboarding) {
      if (storedTemplateData) {
        dispatch(setCustomTemplateSelectedTab(TabType.CREATE));
        if (storedTemplateData.id && !storedTemplateData.isFromTemplate) {
          setIsEditing(true);
          setCurrentTemplate({
            ...storedTemplateData,
          } as CustomNote);
        } else {
          setIsEditing(false);
          setCurrentTemplate({
            name: storedTemplateData.name,
            document: storedTemplateData.document,
            from_template: storedTemplateData.id,
            settings: {
              is_addon: false,
              is_cpt_codes: false,
            },
          } as CustomNote);
        }
        dispatch(setAITemplateModalState(true));
        dispatch(setStoredTemplateData(null));
      }
    }
  }, [storedTemplateData, dispatch, userInfo, needsOnboarding]);

  const handleDrawerClose = () => {
    dispatch(setAITemplateModalState(false));
    dispatch(setCustomTemplateSelectedTab(TabType.LIST));
    setCurrentTemplate(null);
    setIsEditing(false);
  };

  function handleEditTemplate(template: CustomNote) {
    setIsEditing(true);
    setCurrentTemplate(template);
    dispatch(setCustomTemplateSelectedTab(TabType.CREATE));
  }

  async function handleGoBack() {
    dispatch(setCustomTemplateSelectedTab(TabType.LIST));
    setCurrentTemplate(null);
    setIsEditing(false);
    await fetchCustomNoteList();
  }

  const goBackFromCreate = async () => {
    dispatch(setCustomTemplateSelectedTab(TabType.LIST));
    await fetchCustomNoteList();
    setCurrentTemplate(null);
    setIsEditing(false);
  };

  const handleSaveTemplate = async () => {
    try {
      if (currentTemplate?.name && currentTemplate?.name.length > 100) {
        message.error("Template name should be less than 100 characters");
        return;
      }
      setIsLoading(true);
      if (currentTemplate) {
        if (isEditing) {
          const res = await updateCustomNote(currentTemplate.pk, {
            name: currentTemplate.name,
            document: currentTemplate.document,
            settings: currentTemplate.settings,
            is_group_accessible: currentTemplate.is_group_accessible,
          });
          if (res.data.status === 2) {
            message.error("Error in updating template. Please try again.");
          } else {
            await fetchCustomNoteList();
            message.success("Your template was successfully updated");
          }
        } else {
          const { name, document, settings, from_template, is_group_accessible } = currentTemplate;
          const res = await createCustomNote({ name, document, settings, from_template, is_group_accessible });
          if (res.data.status === 2) {
            message.error("Error in creating template. Please try again.");
          } else {
            await fetchCustomNoteList();
            message.success(`Your template ${name} was successfully created`);
          }
          if (templateOpenedFromNoteModal) {
            dispatch(setTemplateOpenedFromNoteModal(false));
            dispatch(setAITemplateModalState(false));
            dispatch(
              setRecordingDetail({
                ...recordingDetail,
                note_type: res.data.type_id,
                note_type_info: {
                  id: res.data.type_id,
                  name: res.data.name,
                },
              }),
            );
          }
        }
        dispatch(setCustomTemplateSelectedTab(TabType.LIST));
        setCurrentTemplate(null);
        setIsEditing(false);
      }
    } catch (err) {
      console.error(err);
      message.error(isEditing ? "Failed to update template" : "Failed to create template");
    } finally {
      setIsLoading(false);
    }
  };

  const drawerFooter = {
    [TabType.CREATE]: (
      <Flex gap={16} justify="flex-end">
        <Button type="default" onClick={goBackFromCreate}>
          Go back
        </Button>
        <Button type="primary" onClick={handleSaveTemplate} loading={isLoading} disabled={isPrimaryButtonDisabled}>
          {isEditing ? "Save changes" : "Create Template"}
        </Button>
      </Flex>
    ),
    [TabType.LIST]:
      customNoteList.length === 0 ? null : (
        <Flex gap={16} justify="flex-end">
          <Button type="default" onClick={() => dispatch(setAITemplateModalState(false))}>
            Close
          </Button>
          <Button
            type="primary"
            onClick={() => {
              dispatch(setCustomTemplateSelectedTab(TabType.CREATE));
            }}
          >
            Create Template
          </Button>
        </Flex>
      ),
  };

  return (
    <Drawer
      closable={false}
      open={isAITemplateModalVisible}
      onClose={handleDrawerClose}
      width={750}
      styles={{
        body: {
          padding: "0",
        },
      }}
      footer={drawerFooter[customTemplateSelectedTab]}
    >
      <>
        {customTemplateSelectedTab === TabType.LIST && (
          <ListContainer
            handleEditTemplate={handleEditTemplate}
            customNoteList={customNoteList}
            fetchCustomNoteList={fetchCustomNoteList}
            isListLoading={initialLoading}
          />
        )}
        {customTemplateSelectedTab === TabType.CREATE && (
          <Create
            setIsPrimaryButtonDisabled={setIsPrimaryButtonDisabled}
            setCurrentTemplate={setCurrentTemplate}
            isEditing={isEditing}
            currentTemplate={currentTemplate}
            handleGoBack={handleGoBack}
          />
        )}
      </>
    </Drawer>
  );
};
